import { InputNumber, Select, Switch, Tooltip } from 'antd'
import CantileverRow from 'components/CantileverRow'
import { getAllItemsToRender, useScatterplotContext } from 'features/timeSeries'
import {
  FadedSpan,
  SectionContent,
  SensorControls,
  SmallText,
} from 'features/timeSeries/sharedStyles'
import React, { useCallback, useEffect, useMemo } from 'react'
import { BaseSensorEntry, ThresholdFilterConfig } from 'types'

const { Option } = Select

interface ThresholdFilteringProps {
  selectedSensors: BaseSensorEntry[]
  sensorEntries: BaseSensorEntry[]
}

export const ThresholdFiltering: React.FC<ThresholdFilteringProps> = ({
  selectedSensors,
  sensorEntries,
}) => {
  const { thresholdFilters, setThresholdFilters, derivedValues } =
    useScatterplotContext()

  // Get all items to render (sensors + derived values with dependencies met)
  const allItemsToRender = useMemo(
    () => getAllItemsToRender(selectedSensors, derivedValues, sensorEntries),
    [selectedSensors, derivedValues, sensorEntries]
  )

  // Initialize filters for selected sensors
  useEffect(() => {
    const newFilters: Record<string, ThresholdFilterConfig> = {}

    // Build list of all items (sensors + derived values with dependencies met)
    const allItems: Array<{ id: string }> = [...selectedSensors]

    // Add derived values that have all dependencies selected
    derivedValues.forEach((derived) => {
      const allDependenciesSelected = derived.data.dependencyIds.every(
        (sensorId) =>
          selectedSensors.some((selected) => selected.id === sensorId)
      )

      if (allDependenciesSelected) {
        allItems.push({ id: derived.id })
      }
    })

    allItems.forEach((item) => {
      if (!thresholdFilters[item.id]) {
        newFilters[item.id] = {
          type: 'threshold_filter',
          sensorId: item.id,
          enabled: false,
          condition: 'above',
          value1: 0,
          value2: undefined,
        }
      }
    })

    if (Object.keys(newFilters).length > 0) {
      setThresholdFilters((prev) => ({ ...prev, ...newFilters }))
    }
  }, [selectedSensors, derivedValues, thresholdFilters, setThresholdFilters])

  const handleThresholdToggle = useCallback(
    (sensorId: string, enabled: boolean) => {
      const updated = {
        ...thresholdFilters,
        [sensorId]: { ...thresholdFilters[sensorId], enabled },
      }
      setThresholdFilters(updated)
    },
    [thresholdFilters, setThresholdFilters]
  )

  const handleThresholdConditionChange = useCallback(
    (
      sensorId: string,
      condition: 'above' | 'below' | 'between' | 'stability'
    ) => {
      const updated = {
        ...thresholdFilters,
        [sensorId]: {
          ...thresholdFilters[sensorId],
          condition,
          // Initialize value2 for stability condition if not set
          value1:
            condition === 'stability' && !thresholdFilters[sensorId].value1
              ? 1
              : thresholdFilters[sensorId].value1,
          value2:
            condition === 'stability' && !thresholdFilters[sensorId].value2
              ? 2
              : thresholdFilters[sensorId].value2,
        },
      }
      setThresholdFilters(updated)
    },
    [thresholdFilters, setThresholdFilters]
  )

  const handleThresholdValueChange = useCallback(
    (sensorId: string, valueIndex: 1 | 2, value: number | null) => {
      const key = valueIndex === 1 ? 'value1' : 'value2'
      const updated = {
        ...thresholdFilters,
        [sensorId]: { ...thresholdFilters[sensorId], [key]: value ?? 0 },
      }
      setThresholdFilters(updated)
    },
    [thresholdFilters, setThresholdFilters]
  )

  return (
    <SectionContent>
      <SmallText type='secondary'>
        Only show data points where conditions are met. Stability filters by
        coefficient of variance (CV).
      </SmallText>

      {allItemsToRender.length > 0 ? (
        <SensorControls>
          {allItemsToRender.map((item) => {
            const filter = thresholdFilters[item.id]
            if (!filter) return null
            return (
              <div key={item.id}>
                <CantileverRow
                  leadingContent={
                    <Tooltip title={item.name}>
                      <span>
                        {item.name}
                        {item.isDerived && <FadedSpan> (derived)</FadedSpan>}
                      </span>
                    </Tooltip>
                  }
                  trailingContent={
                    <Switch
                      checked={filter.enabled}
                      onChange={(checked) =>
                        handleThresholdToggle(item.id, checked)
                      }
                    />
                  }
                />

                {filter.enabled && (
                  <>
                    <CantileverRow
                      leadingContent={
                        <Select
                          value={filter.condition}
                          onChange={(value) =>
                            handleThresholdConditionChange(
                              item.id,
                              value as
                                | 'above'
                                | 'below'
                                | 'between'
                                | 'stability'
                            )
                          }>
                          <Option value='above'>Above</Option>
                          <Option value='below'>Below</Option>
                          <Option value='between'>Between</Option>
                          <Option value='stability'>
                            <Tooltip title='Filter by coefficient of variance (CV). Shows only points where CV ≥ threshold within the specified window.'>
                              Stability (CV)
                            </Tooltip>
                          </Option>
                        </Select>
                      }
                      trailingContent={
                        <InputNumber
                          value={filter.value1}
                          step={0.1}
                          onChange={(value) =>
                            handleThresholdValueChange(
                              item.id,
                              1,
                              typeof value === 'number' ? value : null
                            )
                          }
                          placeholder={
                            filter.condition === 'stability' ? 'CV %' : 'Value'
                          }
                          min={filter.condition === 'stability' ? 0 : undefined}
                          max={
                            filter.condition === 'stability' ? 100 : undefined
                          }
                        />
                      }
                    />
                    {filter.condition === 'between' && (
                      <CantileverRow
                        trailingContent={
                          <>
                            <span>and</span>
                            <InputNumber
                              value={filter.value2}
                              onChange={(value) =>
                                handleThresholdValueChange(
                                  item.id,
                                  2,
                                  typeof value === 'number' ? value : null
                                )
                              }
                              placeholder='Value 2'
                            />
                          </>
                        }
                      />
                    )}
                    {filter.condition === 'stability' && (
                      <CantileverRow
                        tPadding='0.25rem'
                        trailingContent={
                          <>
                            <span>window</span>
                            <InputNumber
                              value={filter.value2 || 10}
                              onChange={(value) =>
                                handleThresholdValueChange(
                                  item.id,
                                  2,
                                  typeof value === 'number' ? value : null
                                )
                              }
                              placeholder='Window'
                              min={2}
                              max={50}
                            />
                          </>
                        }
                      />
                    )}
                  </>
                )}
              </div>
            )
          })}
        </SensorControls>
      ) : (
        <SmallText type='secondary'>No sensors selected</SmallText>
      )}
    </SectionContent>
  )
}
